import React, { useState, useEffect, useMemo } from "react";
import axios from "axios";
import forkIcon from "./assets/fork_icon.png";

const API_BASE_URL = "https://dishfind.onrender.com";

function DishSearch() {
  const [dishName, setDishName] = useState("");
  const [location, setLocation] = useState("");
  const [radius, setRadius] = useState(10);
  const [isLoading, setIsLoading] = useState(false);
  const [loadingState, setLoadingState] = useState("initial");
  const [loadingProgress, setLoadingProgress] = useState(0);
  const [loadingTips, setLoadingTips] = useState([]);
  const [results, setResults] = useState(null);
  const [error, setError] = useState("");

  // Array of loading tips to display during the search process
  // Wrap in useMemo to prevent recreation on every render
  const tips = useMemo(() => [
    "We're searching for the best restaurants serving your dish...",
    "Analyzing reviews to find the highest-rated options...",
    "Looking for authentic flavors and local favorites...",
    "Checking recent customer experiences...",
    "Finding restaurants with the best version of your dish...",
    "Sorting through hundreds of reviews to find the perfect recommendation...",
    "Almost there! Preparing your personalized restaurant list...",
    "Did you know? Our AI analyzes thousands of reviews to find the best dishes.",
    "Great food is worth the wait! We're finding the best options for you.",
    "Searching for hidden gems and local favorites in your area...",
  ], []); // Empty dependency array means this will only be created once

  // Function to update loading tips periodically
  useEffect(() => {
    let tipInterval;
    let progressInterval;

    if (isLoading) {
      // Initialize with a random tip
      setLoadingTips([tips[Math.floor(Math.random() * tips.length)]]);

      // Change tip every 8 seconds
      tipInterval = setInterval(() => {
        const newTip = tips[Math.floor(Math.random() * tips.length)];
        setLoadingTips((prevTips) => {
          // Keep only the last 2 tips to show a history
          const updatedTips = [...prevTips, newTip];
          return updatedTips.slice(Math.max(0, updatedTips.length - 2));
        });
      }, 8000);

      // Simulate progress for better user experience
      progressInterval = setInterval(() => {
        setLoadingProgress((prev) => {
          // Cap progress based on loading state
          const maxProgress =
            loadingState === "fetching"
              ? 30
              : loadingState === "analyzing"
              ? 70
              : loadingState === "finalizing"
              ? 90
              : 95;

          return Math.min(prev + Math.random() * 5, maxProgress);
        });
      }, 3000);
    } else {
      setLoadingProgress(0);
      setLoadingTips([]);
    }

    return () => {
      clearInterval(tipInterval);
      clearInterval(progressInterval);
    };
  }, [isLoading, loadingState, tips]); // Added tips to the dependency array

  // Validate inputs before submission
  const validateInputs = () => {
    if (!dishName.trim()) {
      setError("Please enter a dish name");
      return false;
    }

    if (!location.trim()) {
      setError("Please enter a location");
      return false;
    }

    return true;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!validateInputs()) {
      return;
    }

    // In the handleSubmit function, after setting isLoading to true:
    setIsLoading(true);
    setLoadingState("fetching");
    setLoadingProgress(5); // Start with a small progress value
    setError("");
    setResults(null);

    try {
      // Prepare request data with more explicit values and defaults
      const requestData = {
        dish: dishName.trim(),
        location: location.trim(),
        radius: parseInt(radius) || 10, // Ensure radius is a number with fallback
        useGeolocation: false, // Explicitly set to false
      };

      // Ensure radius is always a valid number
      requestData.radius = Number(requestData.radius);
      if (isNaN(requestData.radius) || requestData.radius <= 0) {
        requestData.radius = 10; // Default to 10km if invalid
      }

      console.log("Sending request data:", JSON.stringify(requestData));

      // Initial request to start searching
      const response = await axios.post(
        `${API_BASE_URL}/api/find-restaurants`,
        requestData
      );

      setLoadingState("analyzing");

      // Poll for results
      const pollInterval = setInterval(async () => {
        try {
          const statusResponse = await axios.get(
            `${API_BASE_URL}/api/status/${response.data.task_id}`
          );

          if (statusResponse.data.state === "ANALYZING") {
            setLoadingState("analyzing");
          } else if (statusResponse.data.state === "FINALIZING") {
            setLoadingState("finalizing");
          } else if (statusResponse.data.state === "COMPLETED") {
            clearInterval(pollInterval);
            setResults(statusResponse.data.result);
            setIsLoading(false);
            setLoadingState("initial");
          } else if (statusResponse.data.state === "FAILED") {
            clearInterval(pollInterval);
            setError(
              statusResponse.data.result.error ||
                "Failed to find restaurants. Please try again."
            );
            setIsLoading(false);
            setLoadingState("initial");
          }
        } catch (error) {
          console.error("Error polling status:", error);
          setError("Failed to get results. Please try again.");
          clearInterval(pollInterval);
          setIsLoading(false);
          setLoadingState("initial");
        }
      }, 2000);
    } catch (error) {
      console.error("Error fetching data:", error);
      setError("Failed to find restaurants. Please try again.");
      setIsLoading(false);
      setLoadingState("initial");
    }
  };

  return (
    <div className="dish-search-container">
      <h2 className="section-title">Find Best Restaurants for Dish</h2>
      <p className="section-subtitle">
        Discover the best places to enjoy your favorite dish
      </p>

      <form className="dish-search-form" onSubmit={handleSubmit}>
        <div className="input-group">
          <label htmlFor="dish-name">Dish Name</label>
          <input
            id="dish-name"
            className="dish-input"
            type="text"
            placeholder="e.g., Biryani, Pizza, Sushi..."
            value={dishName}
            onChange={(e) => setDishName(e.target.value)}
            required
          />
        </div>

        <div className="input-group">
          <label htmlFor="location">Location</label>
          <input
            id="location"
            className="location-input"
            type="text"
            placeholder="e.g. Kochi,Kerala..."
            value={location}
            onChange={(e) => setLocation(e.target.value)}
            required
          />
        </div>

        <div className="input-group">
          <label htmlFor="radius">Radius (km)</label>
          <input
            id="radius"
            className="radius-input"
            type="number"
            min="1"
            max="50"
            value={radius}
            onChange={(e) => setRadius(e.target.value)}
          />
        </div>

        <button className="search-button" type="submit" disabled={isLoading}>
          {isLoading ? (
            <>
              <div className="loading-spinner"></div>
              {loadingState === "fetching"
                ? "Searching..."
                : loadingState === "analyzing"
                ? "Analyzing restaurants..."
                : loadingState === "finalizing"
                ? "Finalizing results..."
                : "Processing..."}
            </>
          ) : (
            <>
              <img src={forkIcon} alt="Fork Icon" className="button-icon" />
              Find Best Restaurants
            </>
          )}
        </button>
      </form>

      {isLoading && (
        <div className="enhanced-loading-container">
          <div className="loading-progress-bar">
            <div
              className="loading-progress-fill"
              style={{ width: `${loadingProgress}%` }}
            ></div>
          </div>

          <div className="loading-state-message">
            {loadingState === "fetching"
              ? "Searching for restaurants..."
              : loadingState === "analyzing"
              ? "Analyzing restaurant reviews..."
              : loadingState === "finalizing"
              ? "Finalizing your recommendations..."
              : "Processing..."}
            <span className="loading-percentage">
              {Math.round(loadingProgress)}%
            </span>
          </div>

          <div className="loading-tips-container">
            {loadingTips.map((tip, index) => (
              <div key={index} className="loading-tip">
                <span className="tip-icon">💡</span> {tip}
              </div>
            ))}
          </div>

          <p className="loading-patience-message">
            This search can take 1-5 minutes as we analyze hundreds of reviews
            to find the best recommendations for you.
          </p>
        </div>
      )}

      <p className="search-note">
        Please note: The search process may take 1-5 minutes to complete as we
        analyze restaurant data for the best recommendations.
      </p>

      {error && <div className="error-message">{error}</div>}

      {results && (
        <div className="restaurant-results">
          <h3 className="results-title">Best Restaurants for {results.dish}</h3>

          {results.restaurants && results.restaurants.length > 0 ? (
            <div className="restaurants-list">
              {results.restaurants.map((restaurant, index) => (
                <div key={index} className="restaurant-card">
                  <div className="restaurant-header">
                    <h4 className="restaurant-name">
                      #{index + 1} {restaurant.name}
                    </h4>
                    <div className="restaurant-rating">
                      <span className="rating-value">
                        {restaurant.rating.toFixed(1)}
                      </span>
                      <span className="rating-stars">★</span>
                      <span className="reviews-count">
                        ({restaurant.reviewsCount} reviews)
                      </span>
                    </div>
                  </div>

                  <p className="restaurant-address">{restaurant.address}</p>

                  {restaurant.analysis && (
                    <div className="dish-analysis">
                      <div className="dish-quality">
                        <span className="quality-label">Dish Quality:</span>
                        <span className="quality-value">
                          {restaurant.analysis.dish_quality || "Unknown"}
                        </span>
                      </div>

                      <p className="dish-description">
                        {restaurant.analysis.dish_description ||
                          "No description available."}
                      </p>

                      {restaurant.analysis.key_points &&
                        restaurant.analysis.key_points.length > 0 && (
                          <ul className="key-points">
                            {restaurant.analysis.key_points.map((point, i) => (
                              <li key={i} className="key-point">
                                {point}
                              </li>
                            ))}
                          </ul>
                        )}

                      {restaurant.analysis.recommendation && (
                        <p className="recommendation">
                          {restaurant.analysis.recommendation}
                        </p>
                      )}
                    </div>
                  )}

                  <a
                    href={restaurant.url}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="view-on-maps"
                  >
                    View on Google Maps
                  </a>
                </div>
              ))}
            </div>
          ) : (
            <p className="no-results">
              No restaurants found for {results.dish} in {results.location}. Try
              a different dish or location.
            </p>
          )}

          <div className="review-prompt">
            <p>How was your meal? Help others decide!</p>
            <a
              href={`https://www.google.com/search?q=review+${encodeURIComponent(
                results.restaurants[0]?.name || "restaurant"
              )}+${encodeURIComponent(results.location)}`}
              target="_blank"
              rel="noopener noreferrer"
              className="review-button"
            >
              <span className="star-icon">★</span> Leave a Google Review for{" "}
              {results.restaurants[0]?.name || "this restaurant"}
            </a>
          </div>
        </div>
      )}
    </div>
  );
}

export default DishSearch;
